import { API_URL } from '../../../config.js';
import type {
  Schedule,
  ScheduleRun,
  ScheduleType,
  SimpleScheduleConfig,
  IntervalScheduleConfig,
  CronScheduleConfig,
  JobDefinition,
} from './types.js';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function arrayField<T>(payload: unknown, key: string): T[] {
  if (!isRecord(payload)) return [];
  const value = payload[key];
  return Array.isArray(value) ? (value as T[]) : [];
}

export async function fetchSchedules(): Promise<{ schedules: Schedule[] }> {
  const res = await fetch(`${API_URL}/schedules?orgId=all`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to fetch schedules');
  const payload: unknown = await res.json();
  return { schedules: arrayField<Schedule>(payload, 'schedules') };
}

export async function fetchScheduleRuns(
  scheduleId: string
): Promise<{ runs: ScheduleRun[] }> {
  const res = await fetch(`${API_URL}/schedules/${scheduleId}/runs?limit=50`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to fetch schedule runs');
  const payload: unknown = await res.json();
  return { runs: arrayField<ScheduleRun>(payload, 'runs') };
}

export async function fetchJobDefinitions(): Promise<{ definitions: JobDefinition[] }> {
  const res = await fetch(`${API_URL}/superadmin/job-definitions?schedulable=true`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to fetch job definitions');
  const payload: unknown = await res.json();
  return { definitions: arrayField<JobDefinition>(payload, 'definitions') };
}

export async function createSchedule(data: {
  jobType: string;
  name: string;
  scheduleType: ScheduleType;
  scheduleConfig: unknown;
  payload: Record<string, unknown>;
  firstRunAt?: string;
  orgId?: string;
}) {
  const res = await fetch(`${API_URL}/schedules`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Failed to create schedule' }));
    throw new Error(error.error || 'Failed to create schedule');
  }
  return res.json();
}

export async function deleteSchedule(scheduleId: string) {
  const res = await fetch(`${API_URL}/schedules/${scheduleId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to delete schedule');
  return res.json();
}

export async function runScheduleNow(scheduleId: string) {
  const res = await fetch(`${API_URL}/schedules/${scheduleId}/run`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to run schedule');
  return res.json();
}

export async function updateSchedule(
  scheduleId: string,
  data: Partial<{
    name: string;
    nextRunAt: Date;
    enabled: boolean;
    scheduleType: ScheduleType;
    scheduleConfig: SimpleScheduleConfig | IntervalScheduleConfig | CronScheduleConfig;
    payload: Record<string, unknown>;
  }>
) {
  const res = await fetch(`${API_URL}/schedules/${scheduleId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Failed to update schedule' }));
    throw new Error(error.error || 'Failed to update schedule');
  }
  return res.json();
}

export async function validateCronPattern(
  pattern: string,
  timezone: string,
  count: number
): Promise<{ valid: boolean; nextRuns?: string[]; error?: string }> {
  const res = await fetch(`${API_URL}/schedules/validate-cron`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ pattern, timezone, count }),
  });
  // An invalid pattern is still a 200 (the route returns { valid: false, error }),
  // so this only catches real transport failures (auth, 5xx, etc).
  if (!res.ok) throw new Error('Failed to validate cron pattern');
  return res.json();
}
